import moment from 'moment'
import { FileText, CheckCircle2 } from 'lucide-react'

// ── Shared read-only display of the data a customer submitted in a task step ──
// Used by both the Requests page and the Onboarding task list to expand a
// completed step and show the previously-submitted values. This is presentation
// only — it never mutates anything and reuses the same value columns the API
// already returns on `taskinstances/get`.

function getFieldOptions(field) {
  const rules = field.validation_rules_snapshot
  if (!rules) return []
  try {
    const parsed = typeof rules === 'string' ? JSON.parse(rules) : rules
    return parsed?.options || []
  } catch {
    return []
  }
}

function fileNameFromUrl(url) {
  if (!url || typeof url !== 'string') return url
  try {
    const clean = url.split('?')[0].split('#')[0]
    const parts = clean.split('/')
    return decodeURIComponent(parts[parts.length - 1]) || url
  } catch {
    return url
  }
}

// Returns an array of human-readable strings for a field's submitted value.
// Empty array means nothing was submitted for this field.
export function getFieldDisplayValues(field) {
  switch (field.field_type_snapshot) {
    case 'BOOLEAN':
      if (field.value_boolean === true) return ['Yes']
      if (field.value_boolean === false) return ['No']
      return []
    case 'DATE':
      return field.value_date ? [moment(field.value_date).format('D MMM YYYY')] : []
    case 'NUMBER':
      return field.value_number != null && field.value_number !== '' ? [String(field.value_number)] : []
    case 'CURRENCY':
      return field.value_number != null && field.value_number !== '' ? ['£' + Number(field.value_number).toFixed(2)] : []
    case 'FILE':
      return field.value_file_url ? [fileNameFromUrl(field.value_file_url)] : []
    case 'DROPDOWN': {
      let v = field.value_json
      if (v == null || v === '') return []
      try { v = typeof v === 'string' ? JSON.parse(v) : v } catch { /* keep raw */ }
      if (v == null || v === '') return []
      const opt = getFieldOptions(field).find(o => o.value === v)
      return [opt ? opt.label : String(v)]
    }
    case 'MULTI_SELECT': {
      let v = field.value_json
      if (!v) return []
      try { v = typeof v === 'string' ? JSON.parse(v) : v } catch { /* keep raw */ }
      if (!Array.isArray(v) || v.length === 0) return []
      const opts = getFieldOptions(field)
      return v.map(val => {
        const opt = opts.find(o => o.value === val)
        return opt ? opt.label : String(val)
      })
    }
    default:
      return field.value_text ? [String(field.value_text)] : []
  }
}

// File names uploaded in a document_upload step. The API stores these as an
// object keyed by file id ({ file_xxx: { filename, status, ... } }); once a step
// is completed only files with status 'completed' remain.
export function getStepFiles(step) {
  const files = step.step_result?.files
  if (!files) return []
  const list = Array.isArray(files) ? files : Object.values(files)
  return list
    .filter(f => f && (typeof f === 'string' || f.status == null || f.status === 'completed'))
    .map(f => (typeof f === 'string' ? f : (f.filename || f.name || f.original_name || f.file_name)))
    .filter(Boolean)
}

// Whether a completed step has anything worth showing when expanded.
export function stepHasSubmittedContent(step) {
  const type = step.step_type_snapshot || 'standard'
  if (type === 'standard') return (step.fields || []).length > 0
  if (type === 'document_upload') return getStepFiles(step).length > 0
  if (type === 'payment') return step.step_result?.amount != null || step.step_config_snapshot?.amount != null
  if (type === 'document_signing') return !!step.step_result?.agreement_id
  return false
}

// Read-only view of what the customer submitted in a single step. The caller
// supplies theme-appropriate text classes so this works on both the themed
// Requests page and the light Onboarding list.
export default function TaskSubmittedValues({
  step,
  labelClassName = 'text-gray-500',
  valueClassName = 'text-gray-900',
  mutedClassName = 'text-gray-400',
}) {
  const type = step.step_type_snapshot || 'standard'

  if (type === 'standard') {
    const fields = [...(step.fields || [])]
      .sort((a, b) => (a.position ?? a.position_snapshot ?? 0) - (b.position ?? b.position_snapshot ?? 0))
    if (fields.length === 0) return null
    return (
      <dl className="space-y-3">
        {fields.map(field => {
          const values = getFieldDisplayValues(field)
          return (
            <div key={field.id}>
              <dt className={`text-xs font-medium ${labelClassName}`}>{field.name_snapshot}</dt>
              {values.length === 0 ? (
                <dd className={`text-sm ${mutedClassName}`}>Not provided</dd>
              ) : values.length === 1 ? (
                <dd className={`text-sm whitespace-pre-wrap break-words ${valueClassName}`}>{values[0]}</dd>
              ) : (
                <dd className={`text-sm ${valueClassName}`}>
                  <ul className="list-disc pl-4 space-y-0.5">
                    {values.map((v, i) => <li key={i} className="break-words">{v}</li>)}
                  </ul>
                </dd>
              )}
            </div>
          )
        })}
      </dl>
    )
  }

  if (type === 'document_upload') {
    const files = getStepFiles(step)
    if (files.length === 0) return <p className={`text-sm ${mutedClassName}`}>No files uploaded</p>
    return (
      <div>
        <p className={`text-xs font-medium mb-1.5 ${labelClassName}`}>Uploaded files</p>
        <ul className="space-y-1.5">
          {files.map((f, i) => (
            <li key={i} className={`flex items-center gap-2 text-sm break-words ${valueClassName}`}>
              <FileText size={14} className={`flex-shrink-0 ${mutedClassName}`} />
              <span className="break-words">{f}</span>
            </li>
          ))}
        </ul>
      </div>
    )
  }

  if (type === 'payment') {
    const amount = step.step_result?.amount ?? step.step_config_snapshot?.amount
    return (
      <div className="flex items-center justify-between">
        <span className={`text-xs font-medium ${labelClassName}`}>Amount paid</span>
        <span className={`text-sm font-semibold ${valueClassName}`}>
          {amount != null ? '£' + parseFloat(amount).toFixed(2) : 'Paid'}
        </span>
      </div>
    )
  }

  if (type === 'document_signing') {
    const name = step.step_result?.agreement_name
    return (
      <div className={`flex items-center gap-2 text-sm ${valueClassName}`}>
        <CheckCircle2 size={14} className="flex-shrink-0 text-green-500" />
        <span className="break-words">Document signed{name ? `: ${name}` : ''}</span>
      </div>
    )
  }

  return null
}
